import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Company from "../../api/Company";
import User from "../../api/User";
import useFormatPhoneNumber from "../../hooks/FormatPhoneNumber";
import { toast } from "react-hot-toast";
import { emitCustomEvent } from "react-custom-events";
import Input from "../../components/form/Input";
import Select from "../../components/form/Select";
import ReactSelect from "react-select";
import { Link, useNavigate, useLocation, useParams } from "react-router-dom";
import Location from "../../api/Location";
import { DashboardLayout } from "../../components/organisms/Layout";
import Region from "../../api/Region";
import { useSelector } from "react-redux";
import { selectRoles, selectUser } from "../../store/authSlice";
import { userTypesNotEnterprise, userTypesSuper } from "../../utils/permission";
import { generatePassword } from "../../utils";

const AllowedUsers = ["super-user"];

const AddUserPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const editMode = location.pathname.search("edit") !== -1;
  const user = useSelector(selectUser);
  const roles = useSelector(selectRoles);
  const [selectedUserType, setSelectedUserType] = useState("owner");
  const [selectedCompany, setSelectedCompany] = useState(-1);
  const [userCompanyId, setUserCompanyId] = useState(null);
  const [userLocationIds, setUserLocationIds] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState([]);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [userPosition, setUserPosition] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState(null);
  const [regions, setRegions] = useState([]);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const allowedCompanySelection =
    roles.length > 0 ? AllowedUsers.includes(roles[0]) : false;
  const userTypes = allowedCompanySelection
    ? user.company.is_enterprise || roles[0] === "super-user"
      ? userTypesSuper
      : userTypesNotEnterprise
    : userTypesNotEnterprise;

  useEffect(() => {
    // Set page title
    document.title = editMode
      ? "HeyHire Portal-Edit User"
      : "HeyHire Portal-Add New User";

    // Clear any existing validation errors
    setValidationErrors(null);

    // Load initial data
    loadCompanies();
    loadLocations();
    loadRegions();

    if (editMode) {
      loadUser();
    }

    // Cleanup function
    return () => {
      setValidationErrors(null);
      setPassword("");
      setConfirmPassword("");
    };
  }, []);

  useEffect(() => {
    // Only clear locations/regions when company changes in create mode
    // In edit mode, we'll restore them after company is set
    if (!editMode) {
      setSelectedLocation([]);
      setSelectedRegions([]);
    }
  }, [selectedCompany, editMode]);

  // Set selectedCompany when companies load in edit mode
  useEffect(() => {
    if (editMode && companies.length > 0 && userCompanyId) {
      setSelectedCompany(userCompanyId);
    }
  }, [companies, editMode, userCompanyId]);

  // Set selectedLocation when locations load and company is set in edit mode
  useEffect(() => {
    if (editMode && locations.length > 0 && selectedCompany !== -1 && userLocationIds.length > 0) {
      const filtered = filteredLocations();
      const selectedLocations = filtered
        .filter((loc) => userLocationIds.includes(parseInt(loc.id)))
        .map((loc) => ({
          value: loc.id,
          label: loc.name,
        }));
      if (selectedLocations.length > 0) {
        setSelectedLocation(selectedLocations);
      }
    }
  }, [locations, selectedCompany, editMode, userLocationIds]);

  const loadUser = () => {
    setLoading(true);

    User.getById(params.userId)
      .then((response) => {
        const tempUser = response.data.data;
        const companyId = tempUser?.company_id ? parseInt(tempUser.company_id) : null;
        // Handle both single location_id and multiple locations array
        let locationIds = [];
        if (tempUser?.locations && Array.isArray(tempUser.locations) && tempUser.locations.length > 0) {
          // Use locations array if available
          locationIds = tempUser.locations.map((loc) => parseInt(loc.id));
        } else if (tempUser?.location_id) {
          // Fallback to single location_id
          locationIds = [parseInt(tempUser.location_id)];
        }
        setUserCompanyId(companyId);
        setUserLocationIds(locationIds);
        setSelectedUserType(tempUser?.roles[0]?.name);
        setFirstName(tempUser?.first_name);
        setLastName(tempUser?.last_name);
        setEmail(tempUser?.email);
        setPhone(tempUser?.phone);
        setUserPosition(tempUser?.position_name);
        setLoading(false);
      })
      .catch((error) => {
        console.log("loadUser -> error", error);
      });
  };

  const loadCompanies = () => {
    Company.getAll().then((response) => {
      setCompanies(response.data.data);

      // Set the default selected company
      if (response.data.data.length > 0 && !editMode) {
        setSelectedCompany(response.data.data[0].id);
      }
    });
  };

  const loadLocations = () => {
    Location.getAll().then((response) => {
      const locationsResponse = response.data.data;
      setLocations(locationsResponse);

      if (locationsResponse.length > 0 && !editMode) {
        /*
        const _tempLocations = filteredLocations();
        let _selectedLocation = [];
        _selectedLocation.push({value: _tempLocations[0]?.id, label: _tempLocations[0]?.name});
        */
        setSelectedLocation(locationsResponse[0].id);
      }
    });
  };

  const loadRegions = () => {
    Region.getAll().then((response) => {
      setRegions(response.data.data);
    });
  };

  const updateUser = async () => {
    setLoading(true);
    setValidationErrors(null);
    const requestBody = {
      role: selectedUserType,
      email: email,
      phone: phone,
      first_name: firstName,
      last_name: lastName,
      company_id: selectedCompany,
    };
    if (selectedUserType === "regional_manager") {
      requestBody.region_ids = selectedRegions?.map((r) => r?.value);
    } else {
      requestBody.location_ids = selectedLocation?.map((sl) => sl?.value);
    }

    User.update(requestBody, params.userId)
      .then(() => {
        toast.success(t("business-users.user-updated"));
        emitCustomEvent("closeModal");
        navigate(-1);
      })
      .catch((error) => {
        if (error.response?.status === 422) {
          // Set validation errors from API
          setValidationErrors(error.response.data.errors);
          // Show toast for validation error
          toast.error("Please check the form for errors");
        } else if (error.response?.status !== 403) {
          toast.error(t("common.something-went-wrong-please-try-again"));
        }
      })
      .then(() => setLoading(false));
  };

  const addUser = async () => {
    // If password is not provided, generate one
    if (!password && !editMode) {
      const generatedPassword = await generatePassword();
      setPassword(generatedPassword);
      return addUser(); // Retry with generated password
    }

    // Validate password if provided
    if (password && !(await checkConfirmPassword())) {
      return;
    }

    setLoading(true);
    setValidationErrors(null);

    const requestBody = {
      role: selectedUserType,
      email: email,
      phone: phone,
      first_name: firstName,
      last_name: lastName,
      company_id: selectedCompany,
      password: password,
    };

    if (selectedUserType === "regional_manager") {
      requestBody.region_ids = selectedRegions.map((r) => r.value);
    } else {
      requestBody.location_ids = selectedLocation?.map((sl) => sl?.value);
    }

    if (selectedUserType === "employee") {
      requestBody.position_name = userPosition;
    }

    User.add(requestBody)
      .then(() => {
        toast.success(t("business-users.user-added"));
        emitCustomEvent("closeModal");
        navigate("/admin/manage-business-users");
      })
      .catch((error) => {
        if (error.response && error.response.status === 422) {
          setValidationErrors(error.response.data.errors);
        } else {
          if (error?.response?.status !== 403) {
            toast.error(t("common.something-went-wrong-please-try-again"));
          }
        }
      })
      .then(() => setLoading(false));
  };

  const filteredLocations = () => {
    return locations?.filter(
      (location) => parseInt(location?.company_id) === parseInt(selectedCompany) || parseInt(location?.company?.id) === parseInt(selectedCompany)
    );
  };

  const checkConfirmPassword = async () => {
    if (!password || password.length < 6) {
      setValidationErrors((prev) => ({
        ...prev,
        password: ["Password must be at least 6 characters long"],
      }));
      return false;
    }

    if (!confirmPassword || confirmPassword !== password) {
      setValidationErrors((prev) => ({
        ...prev,
        confirmPassword: ["Passwords do not match"],
      }));
      return false;
    }

    setValidationErrors((prev) => ({
      ...prev,
      password: null,
      confirmPassword: null,
    }));
    return true;
  };

  const filteredRegions = () => {
    return regions.filter((region) => region.company_id === selectedCompany);
  };

  return (
    <DashboardLayout >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4">
            <Link
              to="/admin/manage-business-users"
              className="flex items-center justify-center w-10 h-10 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <i className="far fa-chevron-left text-gray-500" />
            </Link>
            <h1 className="text-2xl font-bold text-gray-900">
              {editMode ? "Edit User" : "Create New User"}
            </h1>
          </div>
          <p className="mt-2 text-sm text-gray-600">
            {editMode
              ? "Update the user information below"
              : "Fill in the information below to create a new user"}
          </p>
        </div>

        {/* Main Content */}
        <div className="relative bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {loading && (
            <div className="absolute inset-0 bg-white/50 backdrop-blur-sm flex items-center justify-center z-50">
              <div className="flex flex-col items-center">
                <i className="fad fa-circle-notch fa-spin text-3xl text-primary-500" />
                <span className="mt-2 text-sm text-gray-600 font-medium">
                  {editMode ? "Updating user..." : "Creating user..."}
                </span>
              </div>
            </div>
          )}
          <div className="p-6">
            <form className="space-y-6">
              {/* Company & User Type Section */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {allowedCompanySelection && (
                  <Select
                    label={t("common.company")}
                    value={selectedCompany}
                    name="company"
                    onChange={(e) => {
                      setSelectedCompany(parseInt(e.target.value));
                    }}
                    disabled={!allowedCompanySelection}
                    className="w-full rounded-lg border-gray-300 focus:border-primary-500 focus:ring-primary-500"
                  >
                    {companies.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </Select>
                )}
                <Select
                  name="userType"
                  label={t("business-users.select-user-type")}
                  value={selectedUserType}
                  error={validationErrors?.name?.[0]}
                  onChange={(e) => setSelectedUserType(e.target.value)}
                  className="w-full rounded-lg border-gray-300 focus:border-primary-500 focus:ring-primary-500"
                >
                  {userTypes.map((u, i) => (
                    <option key={i} value={u.value}>
                      {u.label}
                    </option>
                  ))}
                </Select>
              </div>

              {/* Regions/Locations Section */}
              <div>
                {selectedUserType === "regional_manager" ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Regions
                    </label>
                    <ReactSelect
                      value={selectedRegions}
                      isMulti
                      className="rounded-lg"
                      theme={(theme) => ({
                        ...theme,
                        borderRadius: 8,
                        colors: {
                          ...theme.colors,
                          primary25: "#F3F4F6",
                          primary: "#6366F1",
                        },
                      })}
                      onChange={(selected) => setSelectedRegions(selected)}
                      options={filteredRegions().map((region) => ({
                        value: region.id,
                        label: region.name,
                      }))}
                      isDisabled={selectedUserType === "owner"}
                    />
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Locations
                    </label>
                    <ReactSelect
                      value={selectedLocation}
                      isMulti
                      className="rounded-lg"
                      theme={(theme) => ({
                        ...theme,
                        borderRadius: 8,
                        colors: {
                          ...theme.colors,
                          primary25: "#F3F4F6",
                          primary: "#6366F1",
                        },
                      })}
                      onChange={(selected) => setSelectedLocation(selected)}
                      options={filteredLocations().map((location) => ({
                        value: location.id,
                        label: location.name,
                      }))}
                    />
                  </div>
                )}
              </div>

              {/* Personal Information Section */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Personal Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Input
                    name="firstName"
                    label={t("business-users.first-name")}
                    type="text"
                    value={firstName}
                    error={validationErrors?.first_name?.[0]}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full rounded-lg"
                    
                  />
                  <Input
                    name="lastName"
                    label={t("business-users.last-name")}
                    type="text"
                    value={lastName}
                    error={validationErrors?.last_name?.[0]}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full rounded-lg"
                    
                  />
                  {selectedUserType === "employee" && (
                    <Input
                      name="position"
                      label={t("business-users.position")}
                      type="text"
                      value={userPosition}
                      error={validationErrors?.position_name?.[0]}
                      onChange={(e) => setUserPosition(e.target.value)}
                      className="w-full rounded-lg"
                      
                    />
                  )}
                  <Input
                    name="email"
                    label={t("common.email")}
                    type="email"
                    value={email}
                    error={validationErrors?.email?.[0]}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-lg"
                    
                  />
                  <Input
                    name="phone"
                    label={t("common.phone")}
                    type="tel"
                    value={useFormatPhoneNumber(phone)}
                    error={validationErrors?.phone?.[0]}
                    onChange={(e) => {
                      if (e.target.value.length < 15) {
                        setPhone(e.target.value);
                      }
                    }}
                    className="w-full rounded-lg"
                    
                  />
                </div>
              </div>

              {/* Password Section */}
              {!editMode && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Set Password
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Input
                      name="password"
                      label={t("common.password")}
                      type="password"
                      value={password}
                      error={validationErrors?.password}
                      onChange={(e) => {
                        setValidationErrors({
                          ...validationErrors,
                          password: null,
                        });
                        setPassword(e.target.value);
                      }}
                      className="w-full rounded-lg"
                      
                    />
                    <Input
                      name="confirmPassword"
                      label={"Confirm " + t("common.password")}
                      type="password"
                      value={confirmPassword}
                      icon="far fa-lock"
                      error={validationErrors?.confirmPassword}
                      onChange={(e) => {
                        setValidationErrors({
                          ...validationErrors,
                          confirmPassword: null,
                        });
                        setConfirmPassword(e.target.value);
                      }}
                      className="w-full rounded-lg"
                      
                    />
                  </div>
                </div>
              )}
            </form>
          </div>

          {/* Action Buttons */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end">
              <Link
                to="/admin/manage-business-users"
                className="flex-1 sm:flex-none px-4 py-2.5 text-sm font-medium rounded-lg border-2 border-gray-300 text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                {t("common.cancel")}
              </Link>
              <button
                className="flex-1 sm:flex-none px-6 py-2.5 text-sm font-medium rounded-lg bg-primary-500 text-white hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-primary-300 disabled:cursor-not-allowed inline-flex items-center justify-center"
                onClick={(e) => {
                  e.preventDefault();
                  if (editMode) {
                    updateUser();
                  } else {
                    addUser();
                  }
                }}
                disabled={loading}
              >
                {loading && <i className="fad fa-circle-notch fa-spin mr-2" />}
                {editMode ? t("business-users.update-user") : t("common.save")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AddUserPage;
